import React from 'react';
import {
  currencyUI,
  currencySchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  radioUI,
  radioSchema,
  numberSchema,
  numberUI,
  textUI,
  textSchema,
  yesNoSchema,
  yesNoUI,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { getArrayUrlSearchParams } from '~/platform/forms-system/src/js/patterns/array-builder/helpers';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  recipientTypeLabels,
  travelLocationLabels,
} from '../../../utils/labels';
import {
  requiredIfMileageReimbursed,
  requiredIfMileageLocationOther,
  transformDate,
} from './helpers';
import content from '../../../utils/content.json';

const nounSingular = 'mileage expense';
const nounPlural = 'mileage expenses';

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask you about unreimbursed mileage that you, your spouse, or
        your dependents paid for.
      </p>
      <p>
        You can report miles that you traveled for medical purposes in a
        privately owned vehicle such as a car, truck, or motorcycle.
      </p>
    </div>
  );
}

function checkIsItemIncomplete(item) {
  return (
    !item?.traveler ||
    ((item.traveler === 'CHILD' || item.traveler === 'OTHER') &&
      !item?.fullNameTraveler) ||
    !item?.travelLocation ||
    (item.travelLocation === 'OTHER' && !item?.otherTravelLocation) ||
    !item?.travelDate ||
    !item?.travelMilesTraveled ||
    (item?.travelReimbursed !== false &&
      item.travelReimbursed === true &&
      !item?.travelReimbursementAmount)
  );
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'mileageExpenses',
  nounSingular,
  nounPlural,
  required: false,
  isItemIncomplete: item => checkIsItemIncomplete(item),
  maxItems: 12,
  text: {
    getItemName: (item, index = 0) => {
      const destination =
        item?.travelLocation === 'OTHER'
          ? item?.otherTravelLocation || travelLocationLabels.OTHER
          : travelLocationLabels[item?.travelLocation] || '';

      return replaceStrValues(content['array-item-name'], [
        index + 1,
        destination,
      ]);
    },
    cardDescription: item => {
      return (
        <>
          <p className="vads-u-margin-y--0">
            {transformDate(item?.travelDate)}
          </p>
          <p className="vads-u-margin-y--0">
            {item?.travelMilesTraveled} miles
          </p>
        </>
      );
    },
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      nounSingular,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      nounSingular,
    ),
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      nounPlural,
      nounSingular,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(content['array-delete-title'], nounSingular),
    deleteYes: content['array-delete-yes'],
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['mileage-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/**
 * This page is skipped on the first loop for required flow
 * Cards are populated on this page above the uiSchema if items are present
 *
 * @returns {PageSchema}
 */
const summaryPage = {
  uiSchema: {
    'view:mileageExpensesList': arrayBuilderYesNoUI(
      options,
      { hint: '' },
      { hint: '' },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:mileageExpensesList': arrayBuilderYesNoSchema,
    },
    required: ['view:mileageExpensesList'],
  },
};

/** @returns {PageSchema} */
const travelerPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['mileage-traveler-page-title'],
      () => {
        const search = getArrayUrlSearchParams();
        const isEdit = search.get('edit');
        if (isEdit) {
          return content['mileage-traveler-edit-description'];
        }
        return null;
      },
    ),
    traveler: radioUI({
      title: content['mileage-traveler-question'],
      labels: recipientTypeLabels,
    }),
    fullNameTraveler: textUI({
      title: content['mileage-traveler-fullname'],
      expandUnder: 'traveler',
      expandUnderCondition: field => field === 'CHILD' || field === 'OTHER',
      required: (formData, index, fullData) => {
        // Adding a check for formData and fullData since formData is sometimes undefined on load
        // and we can't rely on fullData for testing
        const mileageExpenses =
          formData?.mileageExpenses ?? fullData?.mileageExpenses;
        const mileageExpense = mileageExpenses?.[index];
        return ['CHILD', 'OTHER'].includes(mileageExpense?.traveler);
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      traveler: radioSchema(Object.keys(recipientTypeLabels)),
      fullNameTraveler: textSchema,
    },
    required: ['traveler'],
  },
};
/** @returns {PageSchema} */
const destinationPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['mileage-destination-page-title'],
    ),
    travelLocation: radioUI({
      title: content['mileage-destination-question'],
      labels: travelLocationLabels,
      descriptions: {
        CLINIC: content['mileage-destination-clinic-description'],
      },
    }),
    otherTravelLocation: textUI({
      title: content['mileage-destination-other'],
      expandUnder: 'travelLocation',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index, fullData) =>
        requiredIfMileageLocationOther(formData, index, fullData),
    }),
    travelMilesTraveled: numberUI({
      title: content['mileage-miles-traveled'],
      max: 9999,
    }),
    travelDate: currentOrPastDateUI({
      title: content['mileage-travel-date'],
      monthSelect: false,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      travelLocation: radioSchema(Object.keys(travelLocationLabels)),
      otherTravelLocation: textSchema,
      travelMilesTraveled: numberSchema,
      travelDate: currentOrPastDateSchema,
    },
    required: ['travelLocation', 'travelMilesTraveled', 'travelDate'],
  },
};

/** @returns {PageSchema} */
const reimbursementPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['mileage-reimbursement-page-title'],
    ),
    travelReimbursed: yesNoUI({
      title: content['mileage-reimbursed-question'],
      hint: content['mileage-reimbursed-hint'],
    }),
    // Required doesn't seem to work set directly on currencyUI.
    travelReimbursementAmount: {
      ...currencyUI({
        title: content['mileage-reimbursed-amount'],
        expandUnder: 'travelReimbursed',
        max: 999999.99,
        expandUnderCondition: field => field === true,
      }),
      'ui:required': (formData, index, fullData) =>
        requiredIfMileageReimbursed(formData, index, fullData),
    },
  },
  schema: {
    type: 'object',
    properties: {
      travelReimbursed: yesNoSchema,
      travelReimbursementAmount: currencySchema,
    },
    required: ['travelReimbursed'],
  },
};

export const mileageExpensesPages = arrayBuilderPages(options, pageBuilder => ({
  mileageExpensesIntro: pageBuilder.introPage({
    title: content['mileage-intro-title'],
    path: 'expenses/mileage',
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  mileageExpensesSummary: pageBuilder.summaryPage({
    title: content['mileage-summary-title'],
    path: 'expenses/mileage/add',
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  mileageExpensesTravelerPage: pageBuilder.itemPage({
    title: content['mileage-traveler-page-title'],
    path: 'expenses/mileage/:index/traveler',
    uiSchema: travelerPage.uiSchema,
    schema: travelerPage.schema,
  }),
  mileageExpensesDestinationPage: pageBuilder.itemPage({
    title: content['mileage-destination-page-title'],
    path: 'expenses/mileage/:index/destination',
    uiSchema: destinationPage.uiSchema,
    schema: destinationPage.schema,
  }),
  mileageExpensesReimbursementPage: pageBuilder.itemPage({
    title: content['mileage-reimbursement-page-title'],
    path: 'expenses/mileage/:index/reimbursement',
    uiSchema: reimbursementPage.uiSchema,
    schema: reimbursementPage.schema,
  }),
}));
